import React, { useState } from 'react';
import { X, Clock, CreditCard, Shield, Bell } from 'lucide-react';
import { useExtendTrial, useToggleUserActive } from '../hooks/useUsers';
import Badge, { statusVariant } from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card from '../components/ui/Card';
import { formatDate, getInitials, getDaysLeft } from '../utils/formatters';
import type { User } from '../types';
import toast from 'react-hot-toast';

interface UserDetailProps {
  user: User;
  onClose: () => void;
}

export default function UserDetail({ user, onClose }: UserDetailProps) {
  const [extendDays, setExtendDays] = useState(7);
  const [showExtend, setShowExtend] = useState(false);

  const extendTrial = useExtendTrial();
  const toggleActive = useToggleUserActive();

  const endDate = user.subscriptionEndDate || user.trialEndDate;
  const daysLeft = endDate ? getDaysLeft(endDate) : 0;
  const maxDays = user.subscriptionStatus === 'TRIAL' ? 30 : 365;
  const progress = endDate ? Math.min(100, (daysLeft / maxDays) * 100) : 0;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-[420px] bg-white z-50 shadow-xl overflow-y-auto animate-[slideInRight_200ms]">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-text-primary">User Details</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface text-text-hint"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Profile */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-white text-lg font-bold">
              {getInitials(user.name, user.phone)}
            </div>
            <div>
              <p className="font-semibold text-text-primary">{user.name || 'No name'}</p>
              <p className="text-sm text-text-secondary">{user.phone}</p>
              <p className="text-xs text-text-hint mt-0.5">
                Member since {formatDate(user.createdAt)}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Badge variant={statusVariant(user.subscriptionStatus)}>
              {user.subscriptionStatus}
            </Badge>
            <Badge variant={user.isActive ? 'success' : 'error'}>
              {user.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>

          {/* Subscription Info */}
          <Card className="p-4 border border-border shadow-none">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4 text-text-hint" />
              <p className="text-sm font-semibold text-text-primary">Subscription Info</p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-hint">Status</span>
                <span className="text-text-primary font-medium">{user.subscriptionStatus}</span>
              </div>
              {endDate && (
                <>
                  <div className="flex justify-between">
                    <span className="text-text-hint">Ends</span>
                    <span className="text-text-primary">{formatDate(endDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-hint">Days Left</span>
                    <span className="text-text-primary font-medium">{daysLeft}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1">
                    <div
                      className="bg-primary rounded-full h-1.5 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Quick Actions */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-text-primary">Quick Actions</p>

            {/* Extend Trial */}
            {user.subscriptionStatus === 'TRIAL' && (
              <div>
                {showExtend ? (
                  <div className="flex items-end gap-2">
                    <Input
                      label="Days to extend"
                      type="number"
                      min={1}
                      max={90}
                      value={extendDays}
                      onChange={(e) => setExtendDays(Number(e.target.value))}
                    />
                    <Button
                      size="sm"
                      loading={extendTrial.isPending}
                      onClick={() =>
                        extendTrial.mutate(
                          { id: user.id, days: extendDays },
                          { onSuccess: () => setShowExtend(false) }
                        )
                      }
                    >
                      Extend
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowExtend(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    icon={<Clock className="w-4 h-4" />}
                    onClick={() => setShowExtend(true)}
                  >
                    Extend Trial
                  </Button>
                )}
              </div>
            )}

            {/* Toggle Active */}
            <div className="flex items-center justify-between bg-surface rounded-btn px-4 py-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-text-hint" />
                <span className="text-sm text-text-primary">Account Active</span>
              </div>
              <button
                onClick={() => {
                  if (user.isActive) {
                    if (window.confirm('Deactivate this user? They will lose access.')) {
                      toggleActive.mutate(user.id);
                    }
                  } else {
                    toggleActive.mutate(user.id);
                  }
                }}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  user.isActive ? 'bg-green-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    user.isActive ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Send Notification */}
            <Button
              variant="secondary"
              size="sm"
              className="w-full"
              icon={<Bell className="w-4 h-4" />}
              onClick={() => toast('Coming soon!', { icon: '🔔' })}
            >
              Send Notification
            </Button>
          </div>

          {/* Payment History */}
          <div>
            <p className="text-sm font-semibold text-text-primary mb-3">Payment History</p>
            <div className="bg-surface rounded-btn p-6 text-center">
              <p className="text-sm text-text-hint">No payment history</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
